import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RelativeTime } from '@/components/RelativeTime';
import type { VpnSession } from '../api';
import { SessionStateBadge } from '../components/Badges';
import { EndSessionButton, SessionSheet } from '../components/SessionSheet';
import { endReasonLabel, formatBytes, minutesLabel, userLabel } from '../format';
import { useReachable, useRegionVpns, useVpnSessions } from '../hooks';
import { useVpnPermissions } from '../permissions';

const PAGE_SIZE = 50;
type Which = 'all' | 'live' | 'ended';

const columns: ResponsiveTableColumn<VpnSession>[] = [
  { key: 'state', label: 'State', render: (s) => <SessionStateBadge session={s} /> },
  {
    key: 'user',
    label: 'User',
    hideMobileLabel: true,
    render: (s) => (
      <div className="min-w-0">
        <div className="truncate">{userLabel(s)}</div>
        <div className="text-xs text-muted-foreground truncate">
          {[s.clientDeviceName, s.appVersion].filter(Boolean).join(' · ')}
        </div>
      </div>
    ),
  },
  { key: 'gateway', label: 'Gateway', render: (s) => s.gatewayName ?? s.gatewayId },
  {
    key: 'started',
    label: 'Started',
    hideAt: 'mobile',
    render: (s) => <RelativeTime value={s.startedAt ?? s.requestedAt} />,
  },
  {
    key: 'ends',
    label: 'Ends',
    render: (s) =>
      s.endedAt ? (
        <span className="text-muted-foreground" title={endReasonLabel(s.endReason)}>
          <RelativeTime value={s.endedAt} />
        </span>
      ) : (
        <RelativeTime value={s.expiresAt} />
      ),
  },
  {
    key: 'traffic',
    label: 'Traffic',
    align: 'right',
    hideAt: 'mobile',
    render: (s) => (
      <span className="tabular-nums" title="From the client / to the client">
        {formatBytes(s.bytesFromClient)} / {formatBytes(s.bytesToClient)}
      </span>
    ),
  },
];

/** The sites the viewer's policies let them connect to: what the app will offer. */
function ReachableSites() {
  const { data } = useReachable();
  if (!data?.length) return null;
  return (
    <section className="bg-card border border-border rounded-lg p-4 mb-4">
      <h2 className="text-sm font-semibold mb-2">Sites you can connect to</h2>
      <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {data.map((site) => (
          <li key={site.regionId}>
            {site.regionName}{' '}
            <span className="text-xs text-muted-foreground">
              {site.available ? `up to ${minutesLabel(site.maxSessionMinutes)}` : site.enabled ? 'no gateway available now' : 'VPN off'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Live and recent sessions: the caller's own, and everyone's with `vpn_session:read`. */
export function VpnSessions() {
  const [params, setParams] = useSearchParams();
  const { canReadVpn, canReadSessions } = useVpnPermissions();
  const which: Which = params.get('live') === 'true' ? 'live' : params.get('live') === 'false' ? 'ended' : 'all';
  const regionId = params.get('regionId') ?? undefined;
  const gatewayId = params.get('gatewayId') ?? undefined;
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = {
    regionId,
    gatewayId,
    live: which === 'all' ? undefined : which === 'live',
    page,
    pageSize: PAGE_SIZE,
  };
  const { data, isLoading, isError } = useVpnSessions(query);
  const sites = useRegionVpns(canReadVpn);
  const sessions = data?.sessions ?? [];
  const selected = sessions.find((s) => s.id === selectedId) ?? null;
  const total = data?.total ?? 0;
  const first = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const last = Math.min(page * PAGE_SIZE, total);

  const setFilter = (key: 'live' | 'regionId' | 'gatewayId', value: string | undefined) => {
    setPage(1);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === undefined) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <PageContainer width="full">
      <PageHeader
        title="Sessions"
        subtitle={
          canReadSessions
            ? 'Every VPN session in this organization, newest first'
            : 'Your VPN sessions, newest first'
        }
      />

      <ReachableSites />

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="grid gap-1.5">
          <Label htmlFor="vpn-sessions-which" className="text-xs">Show</Label>
          <Select
            value={which}
            onValueChange={(v) => { setFilter('live', v === 'all' ? undefined : String(v === 'live')); }}
          >
            <SelectTrigger id="vpn-sessions-which" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sessions</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="ended">Ended</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canReadVpn ? (
          <div className="grid gap-1.5">
            <Label htmlFor="vpn-sessions-site" className="text-xs">Site</Label>
            <Select
              value={regionId ?? 'all'}
              onValueChange={(v) => {
                setFilter('regionId', v === 'all' ? undefined : v);
              }}
            >
              <SelectTrigger id="vpn-sessions-site" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every site</SelectItem>
                {(sites.data ?? []).map((r) => (
                  <SelectItem key={r.regionId} value={r.regionId}>
                    {r.regionName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        {gatewayId ? (
          <Button
            variant="ghost"
            onClick={() => {
              setFilter('gatewayId', undefined);
            }}
          >
            Only one gateway&rsquo;s · show all
          </Button>
        ) : null}
      </div>

      <ResponsiveTable
        columns={columns}
        rows={sessions}
        rowKey={(s) => s.id}
        isLoading={isLoading}
        onRowClick={(s) => { setSelectedId(s.id); }}
        rowActions={(s) => <EndSessionButton session={s} />}
        empty={
          isError ? (
            <EmptyState variant="error" size="inline" title="Couldn't load sessions" />
          ) : (
            <EmptyState
              size="inline"
              title="No sessions"
              description="A session appears here when someone connects through a gateway with the VPN on."
            />
          )
        }
      />

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <span>
            {first}–{last} of {total}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { setPage((p) => p - 1); }}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={last >= total} onClick={() => { setPage((p) => p + 1); }}>
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <SessionSheet session={selected} onOpenChange={(open) => { if (!open) setSelectedId(null); }} />
    </PageContainer>
  );
}
